import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, btnSecondary } from '../styles/theme.js'
import {
  sessions as sessionsApi, tags as tagsApi, rubrics as rubricsApi,
  highlights as highlightsApi, highlightTypes as hlTypesApi, comments as commentsApi,
  sessionRubrics as sessionRubricsApi, projects as projectsApi, predictions as predictionsApi,
  bookmarksApi, piiScan as piiScanApi,
} from '../lib/api.js'
import { ChatRenderer } from '../prosemirror/chat-renderer.js'
import { TagPalette, buildShortcutMap } from '../components/tag-palette.js'
import { EvalPanel } from '../components/eval-panel.js'
import { CommentsPanel } from '../components/comments-panel.js'
import { realtime } from '../lib/realtime.js'
import { SimilarPairsPanel } from '../components/similar-sessions.js'

// --- Styles ---

const MOBILE = '(max-width: 768px)'

const sessionWrap = css`
  display: flex;
  height: calc(100vh - 80px);
  overflow: hidden;
  @media ${MOBILE} {
    flex-direction: column;
  }
`

const chatPanel = css`
  flex: 1;
  overflow-y: auto;
  min-width: 0;
`

const chatHeader = css`
  position: sticky;
  top: 0;
  z-index: 10;
  background: ${colors.bgCard};
  border-bottom: 1px solid ${colors.border};
  padding: ${space.sm} ${space.lg};
  display: flex;
  align-items: center;
  gap: ${space.md};
`

const backBtn = css`
  ${btnSecondary};
  padding: ${space.xs} ${space.sm};
  font-size: ${font.size.sm};
`

const headerTitle = css`
  font-size: ${font.size.base};
  font-weight: ${font.weight.semibold};
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const progressCounter = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  white-space: nowrap;
`

const navBtnGroup = css`
  display: flex;
  gap: ${space.xs};
`

const navBtn = css`
  ${btnSecondary};
  padding: ${space.xs} ${space.sm};
  font-size: ${font.size.sm};
  min-width: 32px;
  justify-content: center;
`

const chatBody = css`
  padding: 0;
`

const sidebar = css`
  width: 320px;
  flex-shrink: 0;
  border-left: 1px solid ${colors.border};
  background: ${colors.bgSidebar};
  overflow-y: auto;
  padding: ${space.lg};
`

const sidebarWide = css`
  width: 400px;
  flex-shrink: 0;
  border-left: 1px solid ${colors.border};
  background: ${colors.bgSidebar};
  overflow-y: auto;
  padding: ${space.lg};
`

const tabBar = css`
  display: flex;
  border-bottom: 1px solid ${colors.border};
  margin: -${space.lg};
  margin-bottom: ${space.lg};
  padding: 0 ${space.lg};
`

const tabBtn = css`
  padding: ${space.sm} ${space.md};
  font-size: ${font.size.sm};
  font-weight: ${font.weight.medium};
  color: ${colors.textMuted};
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
  &:hover { color: ${colors.text}; }
`

const tabBtnActive = css`
  color: ${colors.brand};
  border-bottom-color: ${colors.brand};
`

const sidebarSection = css`
  margin-bottom: ${space.lg};
`

const sidebarTitle = css`
  font-size: ${font.size.xs};
  font-weight: ${font.weight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${colors.textMuted};
  margin-bottom: ${space.sm};
`

const metaRow = css`
  display: flex;
  justify-content: space-between;
  font-size: ${font.size.sm};
  padding: 3px 0;
`

const metaLabel = css`
  color: ${colors.textMuted};
`

const metaValue = css`
  color: ${colors.text};
  font-weight: ${font.weight.medium};
`

const placeholder = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  font-style: italic;
`

const systemToggle = css`
  font-size: ${font.size.xs};
  color: ${colors.textSecondary};
  cursor: pointer;
  padding: ${space.xs} 0;
  user-select: none;
  &:hover { color: ${colors.text}; }
`

const loadingStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${colors.textMuted};
`

const autoAdvanceToggle = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  margin-top: ${space.sm};
  cursor: pointer;
  user-select: none;
`

const checkbox = css`
  width: 14px;
  height: 14px;
  accent-color: ${colors.brand};
`

// --- Mobile styles ---

const mobileTabBar = css`
  display: none;
  @media ${MOBILE} {
    display: flex;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 100;
    background: ${colors.bgCard};
    border-top: 1px solid ${colors.border};
    padding: ${space.xs} 0;
    padding-bottom: calc(${space.xs} + env(safe-area-inset-bottom, 0px));
  }
`

const mobileTabItem = css`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${space.sm} 0;
  background: none;
  border: none;
  border-top: 2px solid transparent;
  color: ${colors.textMuted};
  font-size: ${font.size.sm};
  font-weight: ${font.weight.medium};
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
  &:hover { color: ${colors.text}; }
`

const mobileTabItemActive = css`
  color: ${colors.brand};
  border-top-color: ${colors.brand};
  font-weight: ${font.weight.semibold};
`

const mobileHidden = css`
  @media ${MOBILE} {
    display: none !important;
  }
`

const mobileSidebarVisible = css`
  @media ${MOBILE} {
    display: block !important;
    width: 100% !important;
    border-left: none !important;
    flex-shrink: 1 !important;
    padding-bottom: 60px;
  }
`

const mobileChatVisible = css`
  @media ${MOBILE} {
    padding-bottom: 60px;
  }
`

// --- Component ---

export function SessionView() {
  let projectId = null
  let sessionId = null
  let session = null
  let allTags = []
  let rubricList = []
  let shortcutMap = new Map()
  let loading = true
  let showSystem = false
  let autoAdvance = false
  let showAllTags = true // controlled by Tab key, passed to TagPalette

  // Bookmark state
  let bookmarked = false

  // Highlight & comment state
  let hlTypes = []
  let memberMap = {}
  let sessionHighlights = []
  let sessionComments = []
  let pendingHighlight = null // { messageId, startOffset, endOffset }
  let scanningPii = false
  let activeHighlightFilter = null
  let selectedMessageId = null
  let presenceClients = []
  let unsubscribers = []
  let sessionRubrics = []
  let activeTab = 'details' // 'details' | 'evaluation'
  let mobileTab = 'session' // 'session' | 'details' | 'evaluation'
  let predicting = false

  // Navigation state
  let sessionIndex = null
  let sessionTotal = null
  let sessionIds = null
  let loadedOffset = 0 // global offset of the first loaded session ID
  let browserParams = null // search/sort/filter from browser view for fetching more pages
  let backRoute = null // where the back button should navigate to
  let loadController = null // AbortController for in-flight loadSession requests
  let projectDataLoaded = false // project-level data (tags, rubrics, hlTypes) cached
  let userRole = null // current user's role in the project (owner/editor/viewer)
  let showUnmasked = false // owner toggle to reveal PII-masked text

  // Restore auto-advance preference
  try {
    autoAdvance = sessionStorage.getItem('autoAdvance') === 'true'
  } catch { /* ignore */ }

  async function loadSession(pid, sid) {
    // Cancel any in-flight requests from a previous loadSession call
    if (loadController) loadController.abort()
    loadController = new AbortController()
    const { signal } = loadController

    loading = true
    activeHighlightFilter = null
    selectedMessageId = null
    showAllTags = true
    m.redraw()

    try {
      const opts = { signal }

      // Single round-trip for all session-specific data
      const detail = await sessionsApi.getDetail(pid, sid, { unmask: showUnmasked, ...opts })
      session = detail.session
      sessionHighlights = detail.highlights
      sessionComments = detail.comments
      sessionRubrics = detail.sessionRubrics || []
      bookmarked = !!detail.bookmarked

      // Project-level data is reused across navigations within the same project
      if (!projectDataLoaded) {
        const [tagList, rubs, hts, proj] = await Promise.all([
          tagsApi.list(pid, opts),
          rubricsApi.list(pid, opts),
          hlTypesApi.list(pid, opts),
          projectsApi.get(pid),
        ])
        allTags = tagList
        rubricList = rubs
        hlTypes = hts
        memberMap = {}
        for (const mem of (proj.members || [])) {
          memberMap[mem.id] = mem.name
        }
        userRole = proj.memberRole || 'viewer'
        projectDataLoaded = true
      }
      shortcutMap = buildShortcutMap(allTags)
    } catch (err) {
      if (err.name === 'AbortError') return // navigated away, ignore
      console.error('Failed to load session:', err)
      session = null
    } finally {
      loading = false
      m.redraw()
    }
  }

  function goBack() {
    m.route.set(backRoute || `/projects/${projectId}/browser`)
  }

  async function fetchMoreSessions(direction) {
    if (!browserParams) return false
    const params = { limit: browserParams.pageSize }
    if (browserParams.search) params.search = browserParams.search
    if (browserParams.sort) params.sort = browserParams.sort
    if (browserParams.filterTagIds) {
      params.tagIds = browserParams.filterTagIds.join(',')
      if (browserParams.tagMode) params.tagMode = browserParams.tagMode
    }
    if (browserParams.excludeTagIds) {
      params.excludeTagIds = browserParams.excludeTagIds.join(',')
    }

    if (direction === 1) {
      const nextOffset = loadedOffset + sessionIds.length
      if (nextOffset >= sessionTotal) return false
      params.offset = nextOffset
      const result = await sessionsApi.list(projectId, params)
      const newIds = (result.sessions || []).map(s => s.id)
      sessionIds = [...sessionIds, ...newIds]
      return newIds.length > 0
    }
    if (direction === -1) {
      if (loadedOffset <= 0) return false
      const prevOffset = Math.max(0, loadedOffset - browserParams.pageSize)
      params.offset = prevOffset
      params.limit = loadedOffset - prevOffset
      const result = await sessionsApi.list(projectId, params)
      const newIds = (result.sessions || []).map(s => s.id)
      sessionIds = [...newIds, ...sessionIds]
      sessionIndex += newIds.length
      loadedOffset = prevOffset
      return newIds.length > 0
    }
    return false
  }

  async function navigateSession(delta) {
    if (!sessionIds || sessionIndex == null) return
    let newIdx = sessionIndex + delta

    // At boundary — try to fetch the next/previous page of IDs
    if (newIdx >= sessionIds.length && sessionTotal > sessionIds.length) {
      if (!await fetchMoreSessions(1)) return
    }
    if (newIdx < 0 && loadedOffset > 0) {
      if (!await fetchMoreSessions(-1)) return
      newIdx = sessionIndex + delta // recalculate after prepend shifted index
    }

    if (newIdx < 0 || newIdx >= sessionIds.length) return
    sessionIndex = newIdx
    const newId = sessionIds[newIdx]
    m.route.set(`/projects/${projectId}/sessions/${newId}`, null, { replace: true })
  }

  function onTagCreated(tag) {
    // Add the new tag to the local list
    allTags = [...allTags, tag]
    shortcutMap = buildShortcutMap(allTags)
    m.redraw()
  }

  function onTagToggled(tagId, isNowActive, skipAutoAdvance = false) {
    // Update local session tags
    if (!session) return
    if (isNowActive) {
      const tag = allTags.find(t => t.id === tagId)
      if (tag && !session.tags.some(t => t.id === tagId)) {
        session.tags.push(tag)
      }
    } else {
      session.tags = session.tags.filter(t => t.id !== tagId)
    }
    m.redraw()

    // Auto-advance to next session after tagging (skip for auto-assigned parents)
    if (autoAdvance && isNowActive && !skipAutoAdvance) {
      setTimeout(() => navigateSession(1), 200)
    }
  }

  // --- Highlight handlers ---

  function onSelectionChange() {
    const sel = window.getSelection()
    if (pendingHighlight && (!sel || sel.isCollapsed)) {
      pendingHighlight = null
      m.redraw()
    }
  }

  function onTextSelected(messageId, startOffset, endOffset) {
    pendingHighlight = { messageId, startOffset, endOffset }
    m.redraw()
  }

  async function createHighlight(highlightTypeId) {
    if (!pendingHighlight) return
    try {
      await highlightsApi.create(projectId, sessionId, {
        messageId: pendingHighlight.messageId,
        highlightTypeId,
        startOffset: pendingHighlight.startOffset,
        endOffset: pendingHighlight.endOffset,
      })
      window.getSelection()?.removeAllRanges()
      pendingHighlight = null
      // Reload to get server-applied masking (offsets shift when content is redacted)
      const ht = hlTypes.find(t => t.id === highlightTypeId)
      if (ht && ht.masks_content) {
        await loadSession(projectId, sessionId)
        return
      }
      // Non-masking: just re-fetch highlights to stay in sync
      const detail = await sessionsApi.getDetail(projectId, sessionId, { unmask: showUnmasked })
      sessionHighlights = detail.highlights
    } catch (err) {
      console.error('Failed to create highlight:', err)
      pendingHighlight = null
    }
    m.redraw()
  }

  function onHighlightClicked(highlightId) {
    activeHighlightFilter = activeHighlightFilter === highlightId ? null : highlightId
    m.redraw()
  }

  async function deleteHighlight(highlightId) {
    try {
      const hl = sessionHighlights.find(h => h.id === highlightId)
      await highlightsApi.delete(projectId, highlightId)
      if (activeHighlightFilter === highlightId) activeHighlightFilter = null
      // Masking highlights change message content — reload to get correct offsets
      if (hl && hl.masks_content) {
        await loadSession(projectId, sessionId)
        return
      }
      sessionHighlights = sessionHighlights.filter(h => h.id !== highlightId)
    } catch (err) {
      console.error('Failed to delete highlight:', err)
    }
    m.redraw()
  }

  // --- Comment handlers ---

  async function postComment(content, highlightId, parentId) {
    try {
      const comment = await commentsApi.create(projectId, sessionId, {
        content,
        highlightId: highlightId || undefined,
        parentId: parentId || undefined,
      })
      sessionComments = [...sessionComments, comment]
    } catch (err) {
      console.error('Failed to post comment:', err)
    }
    m.redraw()
  }

  async function deleteComment(commentId) {
    try {
      await commentsApi.delete(projectId, commentId)
      sessionComments = sessionComments.filter(c => c.id !== commentId)
    } catch (err) {
      console.error('Failed to delete comment:', err)
    }
    m.redraw()
  }

  function onKeyDown(e) {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return

    if (e.key === 'Escape') {
      e.preventDefault()
      if (pendingHighlight) {
        pendingHighlight = null
        window.getSelection()?.removeAllRanges()
        m.redraw()
        return
      }
      goBack()
    } else if (e.key === 'ArrowRight' && e.shiftKey) {
      e.preventDefault()
      navigateSession(1)
    } else if (e.key === 'ArrowLeft' && e.shiftKey) {
      e.preventDefault()
      navigateSession(-1)
    } else if (e.key === 'Tab') {
      e.preventDefault()
      showAllTags = !showAllTags
      m.redraw()
    } else if (session && shortcutMap.has(e.key.toLowerCase()) && !e.ctrlKey && !e.metaKey && !e.altKey) {
      // Keyboard tagging
      e.preventDefault()
      const tagId = shortcutMap.get(e.key.toLowerCase())
      const isActive = session.tags?.some(t => t.id === tagId)

      if (isActive) {
        tagsApi.remove(projectId, sessionId, tagId).then(() => {
          onTagToggled(tagId, false)
        }).catch(err => {
          console.error('Failed to remove tag:', err)
        })
      } else {
        // Auto-assign parent if this is a subtag
        const tag = allTags.find(t => t.id === tagId)
        const parentNeedsAssign = tag?.parent_id && !session.tags?.some(t => t.id === tag.parent_id)

        const promises = []
        if (parentNeedsAssign) {
          promises.push(
            tagsApi.assign(projectId, sessionId, tag.parent_id).then(() => {
              onTagToggled(tag.parent_id, true, true) // skipAutoAdvance
            })
          )
        }
        promises.push(
          tagsApi.assign(projectId, sessionId, tagId).then(() => {
            onTagToggled(tagId, true)
          })
        )
        Promise.all(promises).catch(err => {
          console.error('Failed to assign tag:', err)
        })
      }
    }
  }

  function getDisplayMessages() {
    if (!session?.messages) return []
    if (showSystem) return session.messages
    return session.messages.filter((msg) => msg.role !== 'system')
  }

  function getSystemCount() {
    if (!session?.messages) return 0
    return session.messages.filter((msg) => msg.role === 'system').length
  }

  function toggleAutoAdvance() {
    autoAdvance = !autoAdvance
    try { sessionStorage.setItem('autoAdvance', String(autoAdvance)) } catch { /* ignore */ }
  }

  function setupRealtime() {
    // Clean up previous subscriptions
    for (const unsub of unsubscribers) unsub()
    unsubscribers = []

    realtime.connect(projectId)
    realtime.setViewing(sessionId)

    unsubscribers.push(realtime.on('presence', (data) => {
      presenceClients = data.clients || []
    }))

    // Live highlight updates from other users
    unsubscribers.push(realtime.on('highlight_created', (data) => {
      if (data.highlight && !sessionHighlights.find(h => h.id === data.highlight.id)) {
        sessionHighlights = [...sessionHighlights, data.highlight]
      }
    }))
    unsubscribers.push(realtime.on('highlight_deleted', (data) => {
      sessionHighlights = sessionHighlights.filter(h => h.id !== data.highlightId)
      if (activeHighlightFilter === data.highlightId) activeHighlightFilter = null
    }))

    // Live comment updates
    unsubscribers.push(realtime.on('comment_created', (data) => {
      if (data.sessionId === sessionId && data.comment) {
        if (!sessionComments.find(c => c.id === data.comment.id)) {
          sessionComments = [...sessionComments, data.comment]
        }
      }
    }))
    unsubscribers.push(realtime.on('comment_deleted', (data) => {
      if (data.sessionId === sessionId) {
        sessionComments = sessionComments.filter(c => c.id !== data.commentId)
      }
    }))

    // Live tag updates — refresh session tags
    unsubscribers.push(realtime.on('tag_assigned', (data) => {
      if (data.sessionId === sessionId && session) {
        const tag = allTags.find(t => t.id === data.tagId)
        if (tag && !session.tags.some(t => t.id === data.tagId)) {
          session.tags = [...session.tags, tag]
        }
      }
    }))
    unsubscribers.push(realtime.on('tag_removed', (data) => {
      if (data.sessionId === sessionId && session) {
        session.tags = session.tags.filter(t => t.id !== data.tagId)
      }
    }))

    // Live session rubric updates
    unsubscribers.push(realtime.on('rubric_applied', (data) => {
      if (data.sessionId === sessionId) {
        sessionRubricsApi.list(projectId, sessionId).then(rubrics => {
          sessionRubrics = rubrics
          m.redraw()
        })
      }
    }))
    unsubscribers.push(realtime.on('rubric_unapplied', (data) => {
      if (data.sessionId === sessionId) {
        sessionRubrics = sessionRubrics.filter(sr => sr.rubric_id !== data.rubricId)
        m.redraw()
      }
    }))
  }

  async function addRubricToSession(rubricId) {
    try {
      await sessionRubricsApi.add(projectId, sessionId, rubricId)
      const rubric = rubricList.find(r => r.id === rubricId)
      if (rubric && !sessionRubrics.some(sr => sr.rubric_id === rubricId)) {
        sessionRubrics = [...sessionRubrics, {
          rubric_id: rubricId,
          title: rubric.title,
          current_version_id: rubric.current_version_id,
        }]
      }
    } catch (err) {
      console.error('Failed to add rubric:', err)
    }
    m.redraw()
  }

  async function removeRubricFromSession(rubricId) {
    try {
      await sessionRubricsApi.remove(projectId, sessionId, rubricId)
      sessionRubrics = sessionRubrics.filter(sr => sr.rubric_id !== rubricId)
    } catch (err) {
      console.error('Failed to remove rubric:', err)
    }
    m.redraw()
  }

  function teardownRealtime() {
    if (loadController) { loadController.abort(); loadController = null }
    for (const unsub of unsubscribers) unsub()
    unsubscribers = []
    realtime.disconnect()
    presenceClients = []
  }

  return {
    oninit(vnode) {
      projectId = vnode.attrs.projectId
      sessionId = vnode.attrs.sessionId

      try {
        const ctx = JSON.parse(sessionStorage.getItem('browserContext') || 'null')
        if (ctx && ctx.projectId === projectId) {
          sessionIds = ctx.sessionIds
          sessionTotal = ctx.total
          sessionIndex = sessionIds ? sessionIds.indexOf(sessionId) : null
          if (sessionIndex === -1) sessionIndex = null
          loadedOffset = (ctx.page || 0) * (ctx.pageSize || 50)
          browserParams = {
            pageSize: ctx.pageSize || 50,
            search: ctx.search,
            sort: ctx.sort,
            filterTagIds: ctx.filterTagIds,
            excludeTagIds: ctx.excludeTagIds,
            tagMode: ctx.tagMode,
          }
          if (ctx.origin === 'cluster' && ctx.clusterId) {
            backRoute = `/projects/${projectId}/clusters/${ctx.clusterId}`
          } else if (ctx.returnUrl) {
            backRoute = ctx.returnUrl
          }
        }
      } catch { /* ignore */ }

      loadSession(projectId, sessionId)
    },

    oncreate() {
      document.addEventListener('keydown', onKeyDown)
      document.addEventListener('selectionchange', onSelectionChange)
      setupRealtime()
    },

    onupdate(vnode) {
      const newSid = vnode.attrs.sessionId
      if (newSid !== sessionId) {
        sessionId = newSid
        realtime.setViewing(sessionId)
        loadSession(projectId, sessionId)
      }
    },

    onremove() {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('selectionchange', onSelectionChange)
      teardownRealtime()
    },

    view(vnode) {
      projectId = vnode.attrs.projectId

      if (loading) {
        return m('div', { class: sessionWrap },
          m('div', { class: loadingStyle }, 'Loading session...'))
      }
      if (!session) {
        return m('div', { class: sessionWrap },
          m('div', { class: loadingStyle }, 'Session not found'))
      }

      const displayMessages = getDisplayMessages()
      const systemCount = getSystemCount()
      const hasNav = sessionIds && sessionIndex != null

      const chatPanelClass = [chatPanel, mobileTab === 'session' ? mobileChatVisible : mobileHidden].join(' ')
      const sidebarBaseClass = activeTab === 'evaluation' ? sidebarWide : sidebar
      const sidebarClass = [sidebarBaseClass, mobileTab !== 'session' ? mobileSidebarVisible : mobileHidden].join(' ')

      return m('div', { class: sessionWrap }, [
        // --- Left panel: chat ---
        m('div', { class: chatPanelClass }, [
          m('div', { class: chatHeader }, (() => {
            const items = [
              m('button', { class: backBtn, onclick: goBack }, 'Back'),
              m('button', {
                class: backBtn,
                style: `font-size: 16px; ${bookmarked ? `color: ${colors.brand};` : ''}`,
                title: bookmarked ? 'Remove bookmark' : 'Bookmark this session',
                onclick: async () => {
                  try {
                    if (bookmarked) {
                      await bookmarksApi.remove(projectId, sessionId)
                      bookmarked = false
                    } else {
                      await bookmarksApi.add(projectId, sessionId)
                      bookmarked = true
                    }
                    m.redraw()
                  } catch (err) {
                    console.error('Bookmark toggle failed:', err)
                  }
                },
              }, bookmarked ? '\u2605' : '\u2606'),
            ]
            // Owner unmask toggle — only shown when there are masking highlights
            if (userRole === 'owner' && sessionHighlights.some(h => h.masks_content)) {
              items.push(m('button', {
                class: backBtn,
                style: `font-size: 13px; ${showUnmasked ? `color: ${colors.brand};` : ''}`,
                title: showUnmasked ? 'Hide PII (re-mask)' : 'Show original PII text',
                onclick: () => {
                  showUnmasked = !showUnmasked
                  loadSession(projectId, sessionId)
                },
              }, showUnmasked ? 'Hide PII' : 'Show PII'))
            }
            if (userRole === 'owner' || userRole === 'editor') {
              items.push(m('button', {
                class: backBtn,
                style: 'font-size: 13px;',
                title: 'Scan this session for PII (emails, phones, SSNs, etc.)',
                disabled: scanningPii,
                onclick: async () => {
                  scanningPii = true
                  m.redraw()
                  try {
                    await piiScanApi.scanSession(projectId, sessionId)
                    // PII highlights mask content — reload to get server-applied redaction
                    await loadSession(projectId, sessionId)
                  } catch (err) {
                    console.error('PII scan failed:', err)
                  } finally {
                    scanningPii = false
                    m.redraw()
                  }
                },
              }, scanningPii ? 'Scanning...' : 'Scan PII'))
            }
            if ((userRole === 'owner' || userRole === 'editor') && sessionRubrics.length > 0) {
              items.push(m('button', {
                class: backBtn,
                style: 'font-size: 13px;',
                onclick: () => m.route.set(`/projects/${projectId}/propagate/${sessionId}`),
                title: 'Clone evaluations to similar sessions',
              }, 'Propagate'))
            }
            items.push(m('div', { class: headerTitle },
              session.external_id || session.id.slice(0, 12)))
            // Presence dots — only users viewing this session
            const here = presenceClients.filter(c => c.viewing === sessionId)
            if (here.length > 0) {
              items.push(m('span', {
                style: `display:flex;gap:2px;align-items:center;`,
                title: here.map(c => c.userName).join(', '),
              }, here.slice(0, 5).map((c, i) =>
                m('span', {
                  key: c.userId,
                  style: `width:8px;height:8px;border-radius:50%;background:${colors.teal};border:1px solid ${colors.bgCard};`,
                })
              )))
            }
            if (hasNav) {
              items.push(m('span', { class: progressCounter },
                `${loadedOffset + sessionIndex + 1} / ${sessionTotal}`))
              items.push(m('div', { class: navBtnGroup }, [
                m('button', {
                  class: navBtn,
                  disabled: loadedOffset + sessionIndex <= 0,
                  onclick: () => navigateSession(-1),
                  title: 'Previous session (Shift+\u2190)',
                }, '\u2190'),
                m('button', {
                  class: navBtn,
                  disabled: loadedOffset + sessionIndex >= sessionTotal - 1,
                  onclick: () => navigateSession(1),
                  title: 'Next session (Shift+\u2192)',
                }, '\u2192'),
                m('span', {
                  style: `font-size: ${font.size.xs}; color: ${colors.textMuted}; margin-left: 4px;`,
                }, 'Shift+\u2190\u2192'),
              ]))
            }
            return items
          })()),
          m('div', { class: chatBody }, [
            systemCount > 0
              ? m('div', {
                  class: systemToggle,
                  style: `padding: ${space.xs} ${space.lg};`,
                  onclick: () => { showSystem = !showSystem },
                }, showSystem
                  ? `Hide ${systemCount} system message${systemCount > 1 ? 's' : ''}`
                  : `Show ${systemCount} system message${systemCount > 1 ? 's' : ''}`)
              : null,
            m(ChatRenderer, {
              messages: displayMessages,
              highlights: sessionHighlights,
              onTextSelected,
              onHighlightClicked,
              onMessageClicked: (msgId) => { selectedMessageId = msgId; m.redraw() },
            }),
          ]),
        ]),

        // --- Right panel: tabbed sidebar ---
        m('div', { class: sidebarClass }, [
          // Tab bar (hidden on mobile, navigation handled by bottom bar)
          m('div', { class: `${tabBar} ${mobileHidden}` }, [
            m('button', {
              class: `${tabBtn} ${activeTab === 'details' ? tabBtnActive : ''}`,
              onclick: () => { activeTab = 'details' },
            }, 'Details'),
            m('button', {
              class: `${tabBtn} ${activeTab === 'evaluation' ? tabBtnActive : ''}`,
              onclick: () => { activeTab = 'evaluation' },
            }, 'Evaluation'),
          ]),

          // Tab content
          activeTab === 'details'
            ? m('div', [
                // Session metadata
                m('div', { class: sidebarSection }, [
                  m('div', { class: sidebarTitle }, 'Session info'),
                  m('div', { class: metaRow }, [
                    m('span', { class: metaLabel }, 'ID'),
                    m('span', { class: metaValue },
                      session.external_id || session.id.slice(0, 12)),
                  ]),
                  session.timestamp
                    ? m('div', { class: metaRow }, [
                        m('span', { class: metaLabel }, 'Date'),
                        m('span', { class: metaValue },
                          new Date(session.timestamp).toLocaleString()),
                      ])
                    : null,
                  m('div', { class: metaRow }, [
                    m('span', { class: metaLabel }, 'Messages'),
                    m('span', { class: metaValue }, session.message_count),
                  ]),
                  session.upload_id
                    ? m('div', { class: metaRow }, [
                        m('span', { class: metaLabel }, 'Upload'),
                        m('span', { class: metaValue }, session.upload_id.slice(0, 8)),
                      ])
                    : null,
                ]),

                // Tags section
                m('div', { class: sidebarSection }, [
                  m('div', { class: sidebarTitle }, 'Tags'),
                  m(TagPalette, {
                    projectId,
                    sessionId,
                    sessionTags: session.tags || [],
                    allTags,
                    onToggle: onTagToggled,
                    onTagCreated,
                    showAll: showAllTags,
                  }),
                  m('button', {
                    class: btnSecondary,
                    style: `margin-top: ${space.sm}; font-size: ${font.size.xs}; width: 100%;`,
                    disabled: predicting,
                    onclick: async () => {
                      predicting = true
                      m.redraw()
                      try {
                        const result = await predictionsApi.predictSession(projectId, sessionId, { granularity: 'session' })
                        if (result.applied?.length > 0) {
                          // Reload session to get updated tags
                          const updated = await sessionsApi.get(projectId, sessionId)
                          if (updated && session) {
                            session.tags = updated.tags
                          }
                        }
                      } catch (err) {
                        console.error('Prediction failed:', err)
                      } finally {
                        predicting = false
                        m.redraw()
                      }
                    },
                  }, predicting ? 'Predicting...' : 'Suggest Tags'),
                  m('label', { class: autoAdvanceToggle, onclick: toggleAutoAdvance }, [
                    m('input', {
                      class: checkbox,
                      type: 'checkbox',
                      checked: autoAdvance,
                    }),
                    'Auto-advance after tagging',
                  ]),
                ]),

                // Highlights section — type buttons for pending selection + existing highlights list
                (() => {
                  const items = []
                  // Highlight type buttons — shown when text is selected
                  if (pendingHighlight && (userRole === 'owner' || userRole === 'editor')) {
                    items.push(m('div', {
                      key: '__hl_apply__',
                      style: `padding:${space.sm} 0; border-bottom:1px solid ${colors.border}; margin-bottom:${space.sm};`,
                    }, [
                      m('div', {
                        style: `font-size:${font.size.xs}; color:${colors.textMuted}; margin-bottom:4px;`,
                      }, 'Highlight selected text as:'),
                      m('div', { style: 'display:flex; flex-wrap:wrap; gap:4px;' },
                        (hlTypes || []).map(ht =>
                          m('button', {
                            key: ht.id,
                            style: `display:flex; align-items:center; gap:4px; padding:3px 8px; border-radius:4px; font-size:${font.size.xs}; font-weight:${font.weight.medium}; border:1px solid ${colors.border}; background:none; cursor:pointer;`,
                            onclick: () => createHighlight(ht.id),
                            title: ht.name,
                          }, [
                            m('span', { style: `width:8px;height:8px;border-radius:50%;background:${ht.color};flex-shrink:0;` }),
                            ht.name,
                          ])
                        )
                      ),
                    ]))
                  }
                  // Existing highlights list
                  const hlItems = sessionHighlights.map(hl =>
                    m('div', {
                      key: hl.id,
                      style: `display:flex; align-items:center; gap:6px; padding:3px 0; font-size:${font.size.xs}; cursor:pointer;`,
                      onclick: () => onHighlightClicked(hl.id),
                    }, [
                      m('span', {
                        style: `width:8px;height:8px;border-radius:50%;background:${hl.type_color};flex-shrink:0;`,
                      }),
                      m('span', {
                        style: `flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; color:${activeHighlightFilter === hl.id ? colors.brand : colors.textSecondary};`,
                      }, hl.type_name),
                      m('button', {
                        style: `border:none;background:none;color:${colors.textMuted};cursor:pointer;font-size:${font.size.xs};padding:0 2px;&:hover{color:${colors.brand};}`,
                        onclick: (e) => { e.stopPropagation(); deleteHighlight(hl.id) },
                        title: 'Remove highlight',
                      }, '\u00d7'),
                    ])
                  )
                  if (items.length === 0 && hlItems.length === 0) return null
                  return m('div', { class: sidebarSection }, [
                    m('div', { key: '__hl_title__', class: sidebarTitle }, `Highlights (${sessionHighlights.length})`),
                    ...items,
                    ...hlItems,
                  ])
                })(),

                // Comments section
                m('div', { class: sidebarSection }, [
                  m('div', { class: sidebarTitle }, 'Comments'),
                  m(CommentsPanel, {
                    comments: sessionComments,
                    highlights: sessionHighlights,
                    activeHighlight: activeHighlightFilter,
                    memberMap,
                    onPost: postComment,
                    onDelete: deleteComment,
                    onFilterChange: (hlId) => { activeHighlightFilter = hlId },
                  }),
                ]),

                // Similar Q&A pairs
                m('div', { class: sidebarSection }, [
                  m(SimilarPairsPanel, { projectId, messageId: selectedMessageId }),
                ]),
              ])
            : m(EvalPanel, {
                projectId,
                sessionId,
                rubricList,
                sessionRubrics,
                _sessionKey: sessionId,
                onAddRubric: addRubricToSession,
                onRemoveRubric: removeRubricFromSession,
              }),
        ]),

        // --- Mobile bottom tab bar ---
        m('div', { class: mobileTabBar }, [
          m('button', {
            class: `${mobileTabItem} ${mobileTab === 'session' ? mobileTabItemActive : ''}`,
            onclick: () => { mobileTab = 'session' },
          }, 'Session'),
          m('button', {
            class: `${mobileTabItem} ${mobileTab === 'details' ? mobileTabItemActive : ''}`,
            onclick: () => { mobileTab = 'details'; activeTab = 'details' },
          }, 'Details'),
          m('button', {
            class: `${mobileTabItem} ${mobileTab === 'evaluation' ? mobileTabItemActive : ''}`,
            onclick: () => { mobileTab = 'evaluation'; activeTab = 'evaluation' },
          }, 'Eval'), 
        ]),
      ])
    },
  }
}
